import React from 'react';
import { Mail, Check, Clock, ShieldAlert } from 'lucide-react';
import Modal from './Modal';

/**
 * One referee, in full.
 *
 * Shown from two places and deliberately identical in both: the Recruitment
 * personal-details step while the reference is being chased, and the Staff
 * record long after the person was hired. A manager asked to evidence safer
 * recruitment two years on should read exactly what the manager who hired them
 * read — so this component takes the same shaped row from either API and knows
 * nothing about which screen opened it.
 *
 * The answers arrive as ready-made label/value pairs (`summary`, built by
 * api/utils/references.js) rather than being labelled here. An employment
 * referee and a character referee are asked different questions, and only the
 * server holds the spec that says which — a rating shown under the wrong
 * question would be worse than no rating at all.
 *
 * Read-only, always. A reference is somebody else's statement; nothing in the
 * organisation may edit it after the fact.
 *
 * @param {object}   reference  a row from buildReferences() + summary, token stripped
 * @param {function} fmtDate    the caller's date formatter
 */
const ReferenceModal = ({ isOpen, onClose, reference, fmtDate = (value) => value }) => {
  if (!reference) return null;

  const typeLabel = reference.type === 'character' ? 'Character' : 'Employment';
  const summary = reference.summary || [];

  /** Where this reference has got to, in one line. */
  const state = reference.received
    ? {
      icon: Check,
      cls: 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-900/20 dark:border-emerald-500/30',
      text: `Reference received${reference.received_at ? ` · ${fmtDate(reference.received_at)}` : ''}`,
    }
    : reference.status === 'Requested'
      ? {
        icon: Clock,
        cls: 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-900/20 dark:border-amber-500/30',
        text: `Form sent${reference.requested_at ? ` · ${fmtDate(reference.requested_at)}` : ''}${reference.requested_by ? ` by ${reference.requested_by}` : ''} — no answer yet`,
      }
      : {
        icon: ShieldAlert,
        cls: 'text-[#64748B] bg-[#F8FAFC] border-[#E3E9F3] dark:text-gray-400 dark:bg-gray-800/40 dark:border-gray-700',
        text: 'Not requested yet',
      };
  const StateIcon = state.icon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${typeLabel} reference`}
      maxWidth="max-w-2xl"
      closeOnBackdrop
    >
      <div className="space-y-5">
        {/* Who they are — the candidate's half of the record, which exists
            whether or not the reference ever came back. */}
        <div>
          <div className="text-[15px] font-semibold text-[#0B1220] dark:text-gray-100">
            {reference.name || 'Name not given'}
          </div>
          {reference.relationship && (
            <div className="text-[13px] text-[#475569] dark:text-gray-300 mt-0.5">{reference.relationship}</div>
          )}
          {reference.email && (
            <div className="flex items-center gap-1.5 text-[13px] text-[#64748B] dark:text-gray-400 mt-1 break-all">
              <Mail className="w-3.5 h-3.5 shrink-0" />
              {reference.email}
            </div>
          )}
        </div>

        <div className={`flex items-start gap-2 rounded-xl border px-3.5 py-2.5 text-[12.5px] font-medium ${state.cls}`}>
          <StateIcon className="w-4 h-4 mt-[1px] shrink-0" />
          <div>
            <div>{state.text}</div>
            {/* The candidate's tickbox is the only authority to contact this
                person, so its absence is stated rather than implied. */}
            {!reference.permission && !reference.received && (
              <div className="mt-0.5 text-rose-600 dark:text-rose-400">
                The candidate did not give permission to contact this referee.
              </div>
            )}
          </div>
        </div>

        {/* What they said. Nothing at all until they have said it — an empty
            grid of headings reads like a reference that came back blank. */}
        {reference.received && summary.length > 0 ? (
          <div className="rounded-xl border border-[#E3E9F3] dark:border-gray-700 divide-y divide-[#F1F5F9] dark:divide-gray-800">
            {summary.map((item) => (
              <div key={item.label} className="px-4 py-3">
                <div className="text-[12px] font-semibold text-[#64748B] dark:text-gray-400 uppercase tracking-wide">
                  {item.label}
                </div>
                <div className="text-[13.5px] text-[#0B1220] dark:text-gray-100 mt-1 whitespace-pre-wrap">
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[13px] text-[#64748B] dark:text-gray-400">
            {reference.received
              ? 'This reference was recorded without any answers.'
              : 'Their answers will appear here once they complete the form.'}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default ReferenceModal;
